
import React, { useMemo, useEffect, useState } from "react";

// Default month labels
const DEFAULT_MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const MONTH_LEFTS = [
  "0%", "8.33%", "16.66%", "24.99%", "33.32%", "41.65%",
  "49.98%", "58.31%", "66.64%", "74.97%", "83.3%", "91.63%",
];

/**
 * Reusable MonthlyBarChart
 * @param {Array} data - Array of data objects
 * @param {function} valueAccessor - Function (item) => {month: 0-11, count: number}
 * @param {string} title - Chart title
 * @param {boolean} loading - Loading state (optional)
 * @param {Array} monthLabels - Array of 12 month labels (optional)
 * @param {string} barColor - Tailwind color class for bars (optional)
 */
function MonthlyBarChart({
  data = [],
  valueAccessor = () => null,
  title = "Monthly Data",
  loading = false,
  monthLabels = DEFAULT_MONTH_LABELS,
  barColor = "bg-teal-500"
}) {
  // Build monthly counts
  const monthlyCounts = useMemo(() => {
    const counts = new Array(12).fill(0);
    if (!data || data.length === 0) return counts;
    for (const item of data) {
      const v = valueAccessor(item);
      if (!v || typeof v.month !== "number" || typeof v.count !== "number") continue;
      if (v.month < 0 || v.month > 11) continue;
      counts[v.month] += v.count;
    }
    return counts;
  }, [data, valueAccessor]);

  const maxCount = monthlyCounts.length ? Math.max(...monthlyCounts) : 0;

  // Animation: empty until data is loaded, then animate to heights
  const [animateOn, setAnimateOn] = useState(false);
  useEffect(() => {
    if (!loading) {
      const id = setTimeout(() => setAnimateOn(true), 60);
      return () => clearTimeout(id);
    } else {
      setAnimateOn(false);
    }
  }, [loading]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold mb-4">{title}</h3>

      <div className="h-64 bg-gray-100 rounded-md flex items-center justify-center">
        <div className="w-full px-4">
          <div className="flex justify-between mb-1">
            <span className="text-sm">{monthLabels[0]}</span>
            <span className="text-sm">{monthLabels[11]}</span>
          </div>

          <div className="relative h-48">
            {monthlyCounts.map((count, i) => {
              const heightPct = !loading && maxCount > 0 ? (count / maxCount) * 100 : 0;
              return (
                <div
                  key={i}
                  className={`absolute bottom-0 ${barColor} rounded-t`}
                  style={{
                    left: MONTH_LEFTS[i],
                    width: "7%",
                    height: animateOn ? `${heightPct}%` : "0%",
                    transition: "height 700ms ease-out",
                  }}
                  aria-label={`Month ${i + 1}: ${count} occurrences`}
                  title={`${count} occurrences`}
                />
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}

export default MonthlyBarChart;
